import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faBoxOpen, faReceipt } from '@fortawesome/free-solid-svg-icons';
import { UI_COLORS, colorHelpers } from '../../utils/colors';
import i18n from '../../i18n';
import { useTranslation } from 'react-i18next';
import SupervisorHistoryButton from '../common/SupervisorHistoryButton';
import AttachmentsPanel from '../common/AttachmentsPanel';
import CoilLineChips from '../coils/CoilLineChips';

const InfoRow = ({ label, value, isBig = false }) => {
    const displayValue = value;

    if (value === null || value === undefined || value === '') return null;

    return (
        <div className="flex flex-col">
            <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: UI_COLORS.TEXT.MUTED }}>
                {label}
            </span>
            <span className={`font-medium mt-0.5 leading-tight ${isBig ? 'text-base' : 'text-[13px]'}`} style={{ color: UI_COLORS.TEXT.PRIMARY }}>
                {displayValue}
            </span>
        </div>
    );
};

const formatDate = (val) => {
    const emDash = i18n.t('common.symbols.emDash');
    if (!val) return emDash;
    try {
        return new Date(val).toLocaleDateString('en-GB', { year: 'numeric', month: 'short', day: 'numeric' });
    } catch (e) {
        return emDash;
    }
};

const formatCurrency = (amount) => {
    if (amount === null || amount === undefined || amount === '') return null;
    return `${i18n.t('common.symbols.currency')}${Number(amount).toFixed(2)}`;
};

const PurchaseAdviceViewModal = ({ isOpen, onClose, data, loading = false }) => {
    const { t } = useTranslation();
    if (!isOpen) return null;

    const emDash = t('common.symbols.emDash');

    const adviceNumber = data && data.advice_number ? data.advice_number : t('purchaseAdvice.view.header.fallbackTitle');
    const supplierName = data && data.supplier_name ? data.supplier_name : emDash;

    const getStatusLabel = (status) => {
        if (!status) return emDash;
        const key = status;
        return (i18n.exists(`purchaseOrders.statuses.${key}`) && t(`purchaseOrders.statuses.${key}`)) ||
            (i18n.exists(`purchaseAdvice.statuses.${key}`) && t(`purchaseAdvice.statuses.${key}`)) ||
            key;
    };

    const statusDisplay = getStatusLabel(data?.status);

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: UI_COLORS.BACKGROUNDS.OVERLAY }}>
            <style>{`.pa-view-modal{ -ms-overflow-style: none; scrollbar-width: none; } .pa-view-modal::-webkit-scrollbar{ display: none; }`}</style>
            <div className="w-full max-w-6xl max-h-[90vh] flex flex-col rounded-2xl bg-white shadow-2xl" style={{ backgroundColor: UI_COLORS.BACKGROUNDS.CARD }}>
                <div className="flex items-center justify-between border-b px-6 py-5 flex-shrink-0" style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}>
                    <div>
                        <p className="text-sm font-semibold uppercase tracking-wide" style={{ color: UI_COLORS.TEXT.MUTED }}>
                            {t('purchaseAdvice.view.header.sectionTitle')}
                        </p>
                        <h2 className="text-2xl font-bold" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
                            {adviceNumber}
                        </h2>
                        <p className="text-sm" style={{ color: UI_COLORS.TEXT.SECONDARY }}>{supplierName}</p>
                    </div>
                    <button
                        onClick={onClose}
                        className="rounded-lg p-2 transition-all duration-200 hover:scale-105"
                        style={{
                            color: UI_COLORS.TEXT.MUTED,
                            backgroundColor: colorHelpers.withOpacity(UI_COLORS.TEXT.MUTED, 0.1),
                        }}
                    >
                        <FontAwesomeIcon icon={faTimes} className="text-lg" />
                    </button>
                </div>

                <div className="pa-view-modal flex-1 overflow-y-auto px-6 py-6 space-y-6">
                    {loading ? (
                        <div className="flex flex-col items-center justify-center py-20">
                            <div className="h-10 w-10 animate-spin rounded-full border-4 border-b-transparent" style={{ borderColor: colorHelpers.withOpacity(UI_COLORS.BRAND.PRIMARY, 0.5) }}></div>
                            <p className="mt-4 text-sm font-semibold" style={{ color: UI_COLORS.TEXT.SECONDARY }}>{t('purchaseAdvice.view.loading')}</p>
                        </div>
                    ) : !data ? (
                        <div className="rounded-xl border px-6 py-16 text-center" style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}>
                            <FontAwesomeIcon icon={faReceipt} className="text-4xl opacity-60" style={{ color: UI_COLORS.TEXT.MUTED }} />
                            <h3 className="mt-4 text-lg font-semibold" style={{ color: UI_COLORS.TEXT.PRIMARY }}>{t('purchaseAdvice.view.empty.title')}</h3>
                            <p className="mt-2 text-sm" style={{ color: UI_COLORS.TEXT.SECONDARY }}>{t('purchaseAdvice.view.empty.message')}</p>
                        </div>
                    ) : (
                        <>

                            {/* Details Grid */}
                            <div className="grid grid-cols-2 gap-x-3 gap-y-3 md:grid-cols-4 p-3 md:p-4 rounded-xl border bg-gray-50/30 mb-6" style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}>
                                <InfoRow label={t('purchaseAdvice.view.fields.adviceNumber')} value={data.advice_number} />
                                <InfoRow label={t('purchaseAdvice.view.fields.status')} value={statusDisplay} />
                                <InfoRow label={t('purchaseAdvice.view.fields.totalQuantity')} value={data.total_quantity} />
                                <InfoRow label={t('purchaseAdvice.view.fields.totalAmount')} value={formatCurrency(data.total_amount)} />

                                <InfoRow label={t('purchaseAdvice.view.fields.adviceDate')} value={formatDate(data.advice_date)} />
                                <InfoRow label={t('common.formFields.labels.dueDate')} value={data.due_date ? formatDate(data.due_date) : null} />
                                <InfoRow label={t('purchaseAdvice.view.fields.poNumber')} value={data.po_number} />
                                <InfoRow label={t('purchaseOrders.view.fields.unloadedByName')} value={data.unloaded_by_name} />
                                <InfoRow 
                                    label={t('purchaseAdvice.view.fields.remark')} 
                                    value={data.remark ? (
                                        <span className="block px-2 py-1 rounded text-xs font-medium bg-yellow-100 text-yellow-800 border border-yellow-200" style={{ display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden', whiteSpace: 'pre-wrap' }}>
                                            {data.remark}
                                        </span>
                                    ) : null} 
                                />
                                <InfoRow
                                    label={t('common.formFields.labels.internalRemarks')}
                                    value={data.internal_remark ? (
                                        <span className="block px-2 py-1 rounded text-xs font-medium bg-green-100 text-green-800 border border-green-200" style={{ display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden', whiteSpace: 'pre-wrap' }}>
                                            {data.internal_remark}
                                        </span>
                                    ) : null}
                                />
                                <InfoRow label={t('common.formFields.labels.emptyTruckWeight')} value={data.empty_truck_weight} />

                                <InfoRow label={t('purchaseAdvice.view.fields.supplierInvoiceNumber')} value={data.supplier_invoice_number} />
                                <InfoRow label={t('purchaseAdvice.view.fields.supplierInvoiceDate')} value={formatDate(data.supplier_invoice_date)} />
                                <InfoRow label={t('purchaseAdvice.view.fields.paymentTerms')} value={data.payment_terms} />
                                <InfoRow label={t('purchaseAdvice.view.fields.pendingQuantity')} value={data.pending_quantity} />

                                <InfoRow label={t('purchaseAdvice.view.fields.vehicleNumber')} value={data.vehicle_number} />
                                <SupervisorHistoryButton label={t('common.formFields.labels.supervisor')} supervisorName={data.supervisor_name} supervisorHistory={data.supervisor_history} />
                                <InfoRow label={t('purchaseAdvice.view.fields.presentShipping')} value={data.present_shipping} />
                                <InfoRow label={t('common.formFields.labels.assignedBy')} value={data.assigned_by_name} />
                                <InfoRow label={t('common.formFields.labels.purchaseAdviceCreatedBy')} value={data.pa_created_by_name} />
                                <InfoRow label={t('common.formFields.labels.purchaseOrderCreatedBy')} value={data.po_created_by_name} />
                                <InfoRow label={t('common.formFields.labels.challanGeneratedBy')} value={data.challan_generated_by_name} />
                            </div>

                            {/* Items List - Tabular format */}
                            <div className="mt-8">
                                <h3 className="text-sm font-semibold uppercase tracking-wide mb-4 flex items-center gap-2" style={{ color: UI_COLORS.TEXT.MUTED }}>
                                    <FontAwesomeIcon icon={faBoxOpen} className="text-xs" />
                                    {t('purchaseAdvice.view.fields.items')}
                                </h3>

                                <div className="rounded-xl border overflow-hidden" style={{ borderColor: UI_COLORS.BORDERS.LIGHT, backgroundColor: UI_COLORS.BACKGROUNDS.CARD }}>
                                    <div className="overflow-x-auto">
                                        <table className="w-full text-left border-collapse whitespace-nowrap">
                                            <thead>
                                                <tr className="border-b" style={{ borderColor: UI_COLORS.BORDERS.LIGHT, backgroundColor: colorHelpers.withOpacity(UI_COLORS.NAVIGATION.HOVER, 0.1) }}>
                                                    <th className="px-4 py-4 text-center text-xs font-bold uppercase tracking-wider text-gray-500">#</th>
                                                    <th className="px-4 py-4 text-xs font-bold uppercase tracking-wider text-gray-500">{t('purchaseAdvice.view.items.product')}</th>
                                                    <th className="px-4 py-4 text-center text-xs font-bold uppercase tracking-wider text-gray-500">{t('common.formFields.labels.pieces')}</th>
                                                    <th className="px-4 py-4 text-center text-xs font-bold uppercase tracking-wider text-gray-500">{t('purchaseAdvice.view.items.quantity')}</th>
                                                    <th className="px-4 py-4 text-right text-xs font-bold uppercase tracking-wider text-gray-500">{t('purchaseAdvice.view.items.price')}</th>
                                                    <th className="px-4 py-4 text-center text-xs font-bold uppercase tracking-wider text-gray-500">{t('purchaseAdvice.view.items.actualQuantity')}</th>
                                                    <th className="px-4 py-4 text-right text-xs font-bold uppercase tracking-wider text-gray-500">{t('purchaseAdvice.view.items.amount')}</th>
                                                    <th className="px-4 py-4 text-center text-xs font-bold uppercase tracking-wider text-gray-500">{t('purchaseAdvice.view.items.remark')}</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y" style={{ divideColor: UI_COLORS.BORDERS.LIGHT }}>
                                                {Array.isArray(data.items) && data.items.length > 0 ? (
                                                    data.items.map((it, idx) => (
                                                        <tr key={idx} className="hover:bg-gray-50/50 transition-colors">
                                                            <td className="px-4 py-4 text-center text-xs font-medium text-gray-400 align-top">
                                                                {idx + 1}
                                                            </td>
                                                            <td className="px-4 py-4 align-top space-y-1">
                                                                <p className="text-base font-bold text-wrap max-w-xs" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
                                                                    {it.size_name || it.product_name || it.item_name || emDash}
                                                                </p>
                                                                {it.size_name && it.product_name && (
                                                                    <span className="text-xs font-medium text-blue-600">
                                                                        {it.product_name}
                                                                    </span>
                                                                )}
                                                              <CoilLineChips item={it} />
                                                            </td>
                                                            {/* Pieces */}
                                                            <td className="px-4 py-4 text-center align-top">
                                                                <p className="text-lg font-bold text-blue-600">
                                                                    {it.actual_pieces || it.quantity_in_pieces || emDash}
                                                                </p>
                                                            </td>
                                                            {/* Order Quantity */}
                                                            <td className="px-4 py-4 text-center align-top">
                                                                <p className="text-sm font-medium" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
                                                                    {it.quantity ? `${it.quantity} ${it.unit || ''}` : emDash}
                                                                </p>
                                                            </td>
                                                            {/* Price */}
                                                            <td className="px-4 py-4 text-right align-top">
                                                                <p className="text-sm font-medium" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
                                                                    {it.price != null ? `${t('common.symbols.currency')}${it.price}` : emDash}
                                                                </p>
                                                            </td>
                                                            {/* Actual Quantity */}
                                                            <td className="px-4 py-4 text-center align-top">
                                                                <p className="text-sm font-semibold text-green-600">
                                                                    {(it.actual_quantity && it.actual_quantity !== '0.00') ? `${it.actual_quantity} ${it.unit || ''}` : emDash}
                                                                </p>
                                                            </td>
                                                            {/* Amount */}
                                                            <td className="px-4 py-4 text-right align-top">
                                                                <p className="text-sm font-bold text-blue-600">
                                                                    {formatCurrency(it.amount) || emDash}
                                                                </p>
                                                            </td>
                                                            {/* Remark */}
                                                            <td className="px-4 py-4 text-center align-top min-w-[160px] max-w-[200px]">
                                                                {it.remark ? (
                                                                    <span className="block px-2 py-1 rounded text-xs font-medium bg-yellow-100 text-yellow-800 border border-yellow-200 text-left" style={{ display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden', whiteSpace: 'pre-wrap' }}>
                                                                        {it.remark}
                                                                    </span>
                                                                ) : emDash}
                                                            </td>
                                                        </tr>
                                                    ))
                                                ) : (
                                                    <tr>
                                                        <td colSpan="8" className="px-4 py-12 text-center text-gray-400">
                                                            {t('purchaseAdvice.view.empty.message')}
                                                        </td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        </>
                    )}

                    {data && (
                        <AttachmentsPanel
                            adviceId={data.id}
                            contextLabel="shared with all advices for this PO"
                        />
                    )}
                </div>

                <div className="flex items-center justify-end gap-3 border-t px-6 py-4 flex-shrink-0" style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}>
                    <button
                        onClick={onClose}
                        className="rounded-xl px-5 py-3 font-semibold transition-all duration-200 hover:scale-105"
                        style={{
                            backgroundColor: 'transparent',
                            color: UI_COLORS.TEXT.MUTED,
                            border: `1px solid ${UI_COLORS.BORDERS.MEDIUM}`,
                        }}
                    >
                        {t('purchaseAdvice.view.buttons.close')}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default PurchaseAdviceViewModal;
